import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CloseIcon } from './Button';
import './ui.css';

// How long a toast stays up (the Task Board's "Task completed · Undo" timing).
export const TOAST_MS = 6000;

/** Toast state: showToast({ text, onUndo? }) replaces any current toast. */
export function useToast() {
    const [toast, setToast] = useState(null);
    const seq = useRef(0);
    const showToast = useCallback(({ text, onUndo }) => {
        seq.current += 1;
        setToast({ key: seq.current, text, onUndo });
    }, []);
    const dismissToast = useCallback(() => setToast(null), []);
    return { toast, showToast, dismissToast };
}

/**
 * The polite live region plus the current toast. The region is always in the
 * DOM so screen readers announce what appears in it. A toast never takes
 * focus; it times out on its own (the timer restarts for a new toast). With an
 * Undo it shows a progress bar that drains over the timeout.
 */
export function ToastRegion({ toast, onDismiss, duration = TOAST_MS }) {
    useEffect(() => {
        if (!toast) return undefined;
        const timer = setTimeout(onDismiss, duration);
        return () => clearTimeout(timer);
    }, [toast, duration, onDismiss]);

    return (
        <div className="v2-toast-region" role="status" aria-live="polite">
            {toast && (
                <div className="v2-toast" key={toast.key}>
                    <span className="v2-toast-ok" aria-hidden="true">
                        <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" focusable="false">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                    </span>
                    <span className="v2-toast-text">{toast.text}</span>
                    {toast.onUndo && (
                        <button
                            type="button"
                            className="v2-toast-undo"
                            onClick={() => { toast.onUndo(); onDismiss(); }}
                        >
                            Undo
                        </button>
                    )}
                    <button type="button" className="v2-toast-x" aria-label="Dismiss" onClick={onDismiss}>
                        <CloseIcon />
                    </button>
                    {toast.onUndo && (
                        <span className="v2-toast-bar" aria-hidden="true">
                            <i style={{ animationDuration: `${duration}ms` }} />
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

ToastRegion.propTypes = {
    toast: PropTypes.shape({
        key: PropTypes.number.isRequired,
        text: PropTypes.node.isRequired,
        onUndo: PropTypes.func,
    }),
    onDismiss: PropTypes.func.isRequired,
    duration: PropTypes.number,
};
